import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { connect } from 'react-redux'
import {
  IFButton,
  IFTable,
  IFText,
  IFsvg,
  IFTooltipIconsLoading,
  IFDialog,
  IFGroupingComponent,
} from 'Components'
import { Colors } from 'Theme'
import Styles from './OperatorUsersInviteList.module.css'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import Skeleton from '@mui/material/Skeleton'
import moment from 'moment'
import InfinityEnums from 'Enums/InfinityEnums'
import OperatorUserSelectors from 'Stores/OperatorUser/Selectors'
import OperatorUserActions from 'Stores/OperatorUser/Actions'
import { IconButton, Box } from '@mui/material'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AuthSelectors from 'Stores/Auth/Selectors'
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import styled from 'styled-components'

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: Colors.white,
    boxShadow: `0px 0px 4px 0px ${Colors.tooltipBoxShadow}`,
    maxWidth: 'none',
    width: 'max-content',
  },
}))
export const useIsTextOverflowing = (ref) => {
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const checkOverflow = () => {
      if (ref.current) {
        setIsOverflowing(ref.current.scrollWidth > ref.current.clientWidth)
      }
    }
    checkOverflow()
    window.addEventListener('resize', checkOverflow)
    return () => {
      window.removeEventListener('resize', checkOverflow)
    }
  }, [ref])

  return isOverflowing
}

const OperatorUsersInviteList = ({
  operatorUsersInvites,
  inviteOperatorUsersPagination,
  fetchInviteOperatorUserListRequestState,
  fetchInviteOperatorUserList,
  tablesHeight,
  deleteInviteOperatorUser,
  deleteInviteOperatorUserRequestState,
  sendOperatorUserEmail,
  sendOperatorUserEmailRequestState,
  loggedOperatorUser,
}) => {
  const { t } = useTranslation()
  const [currentResendRow, setCurrentResendRow] = useState(null)
  const confirmDeleteDialog = useRef()
  const [failedRequest, setFailedRequest] = useState(false)
  const [deleteInviteId, setDeleteInviteId] = useState(null)

  const editPermission = loggedOperatorUser.permissions.includes(
    InfinityEnums.OperatorUserPermission.CAN_EDIT_OPERATOR_USER,
  )

  const loadMoreData = () => {
    if (fetchInviteOperatorUserListRequestState === RequestState.LOADING_MORE)
      return
    fetchInviteOperatorUserList(inviteOperatorUsersPagination)
  }

  useEffect(() => {
    if (
      fetchInviteOperatorUserListRequestState ===
        RequestState.ERROR_0_NETWORK ||
      fetchInviteOperatorUserListRequestState ===
        RequestState.ERROR_400_OCCURRED ||
      fetchInviteOperatorUserListRequestState ===
        RequestState.ERROR_401_OCCURRED ||
      fetchInviteOperatorUserListRequestState ===
        RequestState.ERROR_403_OCCURRED ||
      fetchInviteOperatorUserListRequestState ===
        RequestState.ERROR_409_OCCURRED ||
      fetchInviteOperatorUserListRequestState ===
        RequestState.ERROR_UNKNOWN_OCCURRED
    ) {
      setFailedRequest(true)
    }
    if (fetchInviteOperatorUserListRequestState === RequestState.SUCCEEDED) {
      setFailedRequest(false)
    }
  }, [fetchInviteOperatorUserListRequestState])
  const handleRetry = () => {
    fetchInviteOperatorUserList(inviteOperatorUsersPagination)
  }

  const transformPermissions = (permissions) => {
    const transformedPermissions = permissions.map((permission) => {
      let lowerCase = permission.toLowerCase()
      lowerCase = lowerCase.replace(/_/g, ' ')
      return lowerCase.charAt(0).toUpperCase() + lowerCase.slice(1)
    })
    return transformedPermissions
  }
  useEffect(() => {
    if (deleteInviteOperatorUserRequestState === RequestState.SUCCEEDED) {
      confirmDeleteDialog.current.dismiss()
    }
  }, [deleteInviteOperatorUserRequestState])

  const transformPickedPermissions = (permissions) => {
    let OperatorUserEnums = Object.keys(InfinityEnums.OperatorUserPermission)
    OperatorUserEnums = transformPermissions(OperatorUserEnums)
    const transformedPermissions = permissions.map(
      (number) => OperatorUserEnums[number - 1],
    )
    return transformedPermissions
  }
  const onResendInviteClickHandler = (index) => {
    const rowId = operatorUsersInvites[index]._id

    setCurrentResendRow(rowId)

    sendOperatorUserEmail(operatorUsersInvites[index].email)
  }

  const onDeleteInviteClickHandler = (index) => {
    setDeleteInviteId(operatorUsersInvites[index]._id)
    confirmDeleteDialog.current.show()
  }
  const handleDeleteInvite = () => {
    deleteInviteOperatorUser(deleteInviteId)

    setDeleteInviteId(null)
  }

  const operatorUsersTableColumns = useMemo(
    () => [
      {
        accessorKey: 'email',
        size: 180,
        enableEditing: false,
        header:
          fetchInviteOperatorUserListRequestState === RequestState.LOADING ||
          inviteOperatorUsersPagination === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('OperatorUserPageContainer.Email')}</IFText>
          ),
        Cell: ({ row }) => {
          const textRef = useRef(null)
          const isOverflowing = useIsTextOverflowing(textRef)

          const emailContent = (
            <div className={Styles.parentFlexContainer}>
              <IFText className={Styles.TableText} ref={textRef}>
                {row.original.email}
              </IFText>
            </div>
          )

          return row.index < operatorUsersInvites.length ? (
            isOverflowing ? (
              <HtmlTooltip
                title={
                  <div>
                    <div className={Styles.infoLeft}>
                      <IFText
                        className={Styles.toolTipText}
                        style={{ color: Colors.black }}
                      >
                        {t('InviteOperatorUserListItem.Email')}
                      </IFText>
                    </div>
                    <div className={Styles.infoRight}>
                      <IFText
                        className={Styles.toolTipText}
                        style={{ color: Colors.tooltipGreyBackground }}
                      >
                        {row.original.email}
                      </IFText>
                    </div>
                  </div>
                }
                placement="right-start"
              >
                {emailContent}
              </HtmlTooltip>
            ) : (
              emailContent
            )
          ) : failedRequest ? null : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'createdOn',
        enableEditing: false,
        header:
          fetchInviteOperatorUserListRequestState === RequestState.LOADING ||
          inviteOperatorUsersPagination === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <div>{t('OperatorUserPageContainer.CreatedAt')}</div>
          ),

        Cell: ({ row }) => {
          return row.index < operatorUsersInvites.length ? (
            <div className={Styles.AdminColumnCreatedOn}>
              <IFText>
                {moment(row.original.createdAt).format('MMM DD, YYYY LTS')}
              </IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'permissions',
        size: 120,
        header:
          fetchInviteOperatorUserListRequestState === RequestState.LOADING ||
          inviteOperatorUsersPagination === 0 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            <IFText>{t('OperatorUserPageContainer.Permissions')}</IFText>
          ),
        muiTableBodyCellProps: {
          sx: {
            overflow: 'visible',
          },
        },

        Cell: ({ row }) => {
          return row.index < operatorUsersInvites.length ? (
            <IFGroupingComponent
              componentText={transformPickedPermissions(
                row?.original?.permissions || [],
              )}
            />
          ) : failedRequest ? (
            <>
              <IFButton
                size="sm"
                color={Colors.red}
                text={t('IFTable.TryAgain')}
                onClick={handleRetry}
              />
            </>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
    ],
    [
      operatorUsersInvites,
      fetchInviteOperatorUserList,
      fetchInviteOperatorUserListRequestState,
      failedRequest,
    ],
  )

  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )

  const renderRowActionsCell = ({ row }) => {
    if (Object.keys(row.original).length > 0) {
      const isLoading =
        sendOperatorUserEmailRequestState === RequestState.LOADING &&
        currentResendRow === row.original._id
      return (
        <Box>
          <div className={Styles.TableIcons}>
            <Tooltip title={t('OperatorUserPageContainer.ResendEmail')}>
              <div className={Styles.CustomBackGround}>
                <IFTooltipIconsLoading
                  isLoading={isLoading}
                  Icon={IFsvg.ResendEmailIcon}
                  FilledIcon={IFsvg.ResendEmailIcon}
                  size={24}
                  animationDisabled={false}
                  onClick={() => {
                    if (!isLoading) onResendInviteClickHandler(row.index)
                  }}
                />
              </div>
            </Tooltip>

            <Tooltip title={t('OperatorUserPageContainer.DeleteInvitation')}>
              <IconButton
                onClick={() => {
                  onDeleteInviteClickHandler(row.index)
                }}
              >
                <div
                  className={Styles.DeleteIcon}
                  style={{ color: Colors.red }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </div>
              </IconButton>
            </Tooltip>
          </div>
        </Box>
      )
    }
    if (failedRequest) {
      return (
        <IFButton
          size="sm"
          color={Colors.red}
          text={t('IFTable.TryAgain')}
          onClick={handleRetry}
        />
      )
    }
  }

  return (
    <div>
      {((fetchInviteOperatorUserListRequestState !== RequestState.SUCCEEDED &&
        !inviteOperatorUsersPagination) ||
        operatorUsersInvites) && (
        <>
          <IFTable
            columns={operatorUsersTableColumns}
            data={operatorUsersInvites}
            fetchMoreOnBottomReached={fetchMoreOnBottomReached}
            paginationOffset={inviteOperatorUsersPagination}
            showSkeleton={
              fetchInviteOperatorUserListRequestState ===
                RequestState.LOADING ||
              (fetchInviteOperatorUserListRequestState !==
                RequestState.SUCCEEDED &&
                inviteOperatorUsersPagination === 0)
            }
            renderRowActionsCell={editPermission ? renderRowActionsCell : ''}
            tableMaxHeight={tablesHeight}
            tableSetHeight={'100%'}
            tableRequestState={fetchInviteOperatorUserListRequestState}
            enableEditing={editPermission && true}
          />
        </>
      )}
      <IFDialog
        ref={confirmDeleteDialog}
        title={t('OperatorUserList.deleteModalTitle')}
        subtitle={t('OperatorUserPageContainer.DeleteInvitationDialog')}
        onAction={handleDeleteInvite}
        actionButtonColor={Colors.OperatorUserListDeleteModalDelete}
        actionButtonText={t('OperatorUserList.deleteModalConfirm')}
        isLoading={
          deleteInviteOperatorUserRequestState === RequestState.LOADING
        }
      />
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchInviteOperatorUserList: (offset) =>
      dispatch(OperatorUserActions.fetchInviteOperatorUserList(offset)),
    deleteInviteOperatorUser: (inviteOperatorUserId) =>
      dispatch(
        OperatorUserActions.deleteInviteOperatorUser(inviteOperatorUserId),
      ),
    sendOperatorUserEmail: (email) =>
      dispatch(OperatorUserActions.sendOperatorUserEmail(email)),
  }
}
const mapStateToProps = (state) => ({
  operatorUsersInvites: OperatorUserSelectors.getInviteOperatorUsers(state),
  fetchInviteOperatorUserListRequestState:
    OperatorUserSelectors.getFetchInviteOperatorUserListRequestState(state),
  inviteOperatorUsersPagination:
    OperatorUserSelectors.getInviteOperatorUsersPagination(state),
  deleteInviteOperatorUserRequestState:
    OperatorUserSelectors.getDeleteInviteOperatorUserRequestState(state),
  sendOperatorUserEmailRequestState:
    OperatorUserSelectors.getSendOperatorUserEmailRequestState(state),
  loggedOperatorUser: AuthSelectors.getOperatorUser(state),
})
OperatorUsersInviteList.propTypes = {
  tablesHeight: PropTypes.number,
  inviteOperatorUsersPagination: PropTypes.number,
  fetchInviteOperatorUserListRequestState: PropTypes.number,
  operatorUsersInvites: PropTypes.array,
  deleteInviteOperatorUser: PropTypes.func,
  deleteInviteOperatorUserRequestState: PropTypes.number,
  sendOperatorUserEmail: PropTypes.func,
  sendOperatorUserEmailRequestState: PropTypes.number,
  loggedOperatorUser: PropTypes.object,
}
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OperatorUsersInviteList)
